/**
 * ChoreCompleteSheet — Plan 02-10 Task 2 / UI-SPEC §"3. Chore complete sheet".
 *
 * Opened from <ChoreCard variant="claimed-by-me"> "Fullfør". Wraps a
 * <BottomSheet> with the photo-proof + submit composition. Bildet er valgfritt:
 * «Send til godkjenning» uten bilde er det samme som den gamle «Hopp over
 * bilde»-knappen, som er fjernet (den gjorde ingenting selv).
 *
 * Utseende (#154, steg 4c, Stell_Fullfor-skissen): tittelen «Fullfør oppgave»
 * i arkets topp, oppgaven som en TaskRow med poengene, og «Bevisbilde» med en
 * stiplet flate. Arket forblir et ark (D9). «Sendt!»-skjermen etter innsending
 * er ny oppførsel 3 i design/HANDOFF.md og er ikke bygget: arket lukkes som før.
 *
 * Photo upload flow (CHORE-07 + CHORE-10):
 *   1. To skjulte <input type="file" accept="image/*">: «Ta bilde» åpner den
 *      med `capture="environment"` (kameraet direkte), «Velg fra album» den
 *      uten `capture`, så et bilde som alt er tatt kan velges. Begge går
 *      gjennom de samme kontrollene nedenfor.
 *   2. Client guards on select:
 *        - file.size > 8 MB        → inline error, photos.error.tooLarge
 *        - !file.type.startsWith('image/') → inline error, photos.error.nonImage
 *      Both reset the input and abort BEFORE any network call.
 *   3. Valid file → POST /api/chores/:id/proof (multipart 'photo' field).
 *      Server returns {proofUuid, url}. We stash the proofUuid locally;
 *      the thumbnail preview uses URL.createObjectURL(file) — NOT the
 *      server URL — because the server file is auth-gated WebP and the
 *      kid's session doesn't necessarily match the auth gate at this
 *      preview moment (admin or claimer only). createObjectURL also
 *      avoids a second network round-trip just to render the thumb.
 *   4. «Bytt bilde» (albumfeltet, som også tilbyr kamera) og «Fjern» ligger
 *      over forhåndsvisningen og bytter eller fjerner beviset.
 *
 * Every photo error above — client guards and upload failures alike —
 * renders INLINE under the tile, never through the global toast queue.
 * Two independent reasons, either sufficient: UI-SPEC §"Error states
 * (Phase 2)" specifies the photo errors as inline; and a toast here is
 * invisible anyway, because ToastViewport sits at z-40 while this
 * sheet's overlay is z-50 with a scrim over it, so the message paints
 * behind the panel (issue #60). Do not route these back through
 * lib/toast — ChoreCompleteSheet.test.tsx asserts the queue stays empty.
 * (The spec's network row also asks for a retry button; not built —
 * the tile itself is re-tappable. See issue #60 for the reasoning.)
 *
 * Submit flow (D-12 / CHORE-07):
 *   - "Send til godkjenning" calls useChoreActions.complete with the
 *     stashed proofUuid (omitted when no photo).
 *   - The complete mutation is optimistic (Plan 09 useChoreActions);
 *     onSuccess closes the sheet. The sheet does NOT need its own
 *     spinner state — useMutation's isPending drives the button label.
 *   - On failure the sheet stays open, so the SUBMIT error goes INLINE above
 *     the button rather than through toast(): ToastViewport is z-40 and this
 *     sheet's overlay is z-50, so a toast raised from here is painted behind
 *     the scrim the kid is looking at (#74).
 *   - L2 celebration (Plan 15 ownership) hooks the chore.completed
 *     event emitted server-side; this sheet doesn't trigger L2 directly.
 *
 * Threat mitigations:
 *   - T-02-33 (race after close): AbortController on every fetch + a
 *     submitting-state lock that disables both buttons while the
 *     complete mutation is in flight.
 *   - T-02-34 (XSS via title_snapshot): React text-node interpolation
 *     only; no dangerouslySetInnerHTML.
 *   - T-02-35 (server-side bypass): client guards are UX only; the
 *     server enforces 8MB + image MIME + auth (Plan 02-03 proofs).
 */
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  BottomSheet,
  Button,
  ChoreIcon,
  Icon,
  PointChip,
  SheetError,
  TaskRow,
} from '../../components/primitives';
import { SectionLabel } from '../../components/primitives/SectionLabel';
import { useChoreActions } from './useChoreActions';
import type { PoolChoreInstance } from './useChoresQuery';

const MAX_PHOTO_BYTES = 8 * 1024 * 1024;

interface ChoreCompleteSheetProps {
  instance: PoolChoreInstance;
  open: boolean;
  onClose: () => void;
}

export function ChoreCompleteSheet({
  instance,
  open,
  onClose,
}: ChoreCompleteSheetProps): ReactNode {
  const { t } = useTranslation();
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const albumInputRef = useRef<HTMLInputElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const actions = useChoreActions();

  const [proofUuid, setProofUuid] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Cleanup blob URLs + abort in-flight uploads on unmount + on close.
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, []);

  // When the sheet closes (open flips to false), reset local state so
  // the next open starts clean. Also abort any in-flight upload.
  useEffect(() => {
    if (open) return;
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setProofUuid(null);
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setPreviewUrl(null);
    setUploading(false);
    setPhotoError(null);
    setSubmitError(null);
  }, [open]);

  const onFileChange = (ev: ChangeEvent<HTMLInputElement>): void => {
    const file = ev.target.files?.[0];
    // Reset the input value so picking the same file twice still fires
    // change. Done synchronously so the UX of "Bytt bilde" works.
    if (ev.target) ev.target.value = '';
    if (!file) return;
    setPhotoError(null);

    // Client-side guards (CHORE-10). Server is the authoritative gate;
    // these are UX-only — fail fast before any byte hits the network.
    if (file.size > MAX_PHOTO_BYTES) {
      setPhotoError(t('photos.error.tooLarge'));
      return;
    }
    if (!file.type.startsWith('image/')) {
      setPhotoError(t('photos.error.nonImage'));
      return;
    }

    // Build the FormData payload + start an AbortController so a sheet-
    // close mid-upload tears the request down (T-02-33 mitigation).
    const fd = new FormData();
    fd.append('photo', file, file.name);
    const ac = new AbortController();
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = ac;
    setUploading(true);

    void (async () => {
      try {
        const r = await globalThis.fetch(`/api/chores/${instance.id}/proof`, {
          method: 'POST',
          credentials: 'same-origin',
          body: fd,
          signal: ac.signal,
        });
        if (!r.ok) {
          setPhotoError(t('photos.error.upload'));
          return;
        }
        const body = (await r.json()) as { proofUuid?: string };
        if (!body.proofUuid) {
          setPhotoError(t('photos.error.upload'));
          return;
        }
        // Replace any existing object URL.
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        const obj = URL.createObjectURL(file);
        objectUrlRef.current = obj;
        setPreviewUrl(obj);
        setProofUuid(body.proofUuid);
      } catch (err) {
        // AbortError on close is expected — silently ignore.
        if ((err as { name?: string }).name === 'AbortError') return;
        // Only a thrown fetch is genuinely "the net is gone". A non-OK
        // status or a malformed body reached the server just fine, so
        // those two keep the cause-neutral string above.
        setPhotoError(t('photos.error.network'));
      } finally {
        setUploading(false);
        abortRef.current = null;
      }
    })();
  };

  const onRemovePhoto = (): void => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = null;
    setPreviewUrl(null);
    setProofUuid(null);
    setPhotoError(null);
  };

  const onSubmit = (): void => {
    setSubmitError(null);
    actions.complete.mutate(
      proofUuid ? { instanceId: instance.id, proofUuid } : { instanceId: instance.id },
      {
        onSuccess: () => onClose(),
        // The hook no longer toasts on complete (#74) — this sheet is the
        // surface, because it is still on screen when the failure lands.
        onError: () => setSubmitError(t('chores.complete.error')),
      },
    );
  };

  const submitting = actions.complete.isPending;
  const lockAll = uploading || submitting;
  const hasPhoto = !!previewUrl;

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('chores.complete.title')}
      snapPoints="full"
    >
      <div className="flex flex-col gap-4">
        {/* Oppgaven som skal fullføres, som raden på tavla. */}
        <TaskRow
          data-testid="chore-complete-task"
          icon={<ChoreIcon code={instance.icon_key ?? 'sparkle'} tintSlot={instance.tint_snapshot} size={44} />}
          title={instance.title_snapshot}
          action={
            <span className="flex justify-end">
              <PointChip points={instance.point_value_snapshot} variant="gain" />
            </span>
          }
        />

        <section aria-labelledby="chore-complete-proof" className="flex flex-col">
          <SectionLabel id="chore-complete-proof" as="h3">
            {t('chores.complete.proof')}
          </SectionLabel>

          {hasPhoto && previewUrl ? (
            <figure className="relative m-0 h-47.5 overflow-hidden rounded-lg border border-card-line">
              <img
                data-testid="chore-complete-photo-preview"
                src={previewUrl}
                alt={t('photos.previewAlt')}
                className="h-full w-full object-cover"
              />
              <div className="absolute right-2.5 bottom-2.5 flex gap-2">
                <Button
                  variant="solid"
                  size={44}
                  data-testid="chore-complete-remove"
                  disabled={lockAll}
                  onClick={onRemovePhoto}
                >
                  {t('photos.remove')}
                </Button>
                <Button
                  variant="solid"
                  size={44}
                  data-testid="chore-complete-swap"
                  disabled={lockAll}
                  onClick={() => albumInputRef.current?.click()}
                >
                  {t('photos.swap')}
                </Button>
              </div>
            </figure>
          ) : (
            <div
              data-testid="chore-complete-photo-tile"
              aria-busy={uploading}
              className="flex h-47.5 flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-secondary-line"
            >
              <Button
                variant="solid"
                size={52}
                data-testid="chore-complete-camera"
                disabled={lockAll}
                onClick={() => cameraInputRef.current?.click()}
              >
                <Icon.Camera size={20} />
                {t('photos.camera')}
              </Button>
              <button
                type="button"
                data-testid="chore-complete-album"
                disabled={lockAll}
                onClick={() => albumInputRef.current?.click()}
                className="min-h-11 px-2 text-sm font-bold text-ink-2 underline underline-offset-2 disabled:opacity-40"
              >
                {t('photos.album')}
              </button>
            </div>
          )}

          {/* Inline bildefeil (#60), aldri en toast. Uten feil rendres
              ingenting, så arkets standardbilde ikke får en tom luke. */}
          {photoError ? (
            <p
              data-testid="chore-complete-photo-error"
              role="alert"
              className="m-0 mt-2 text-center text-sm font-semibold text-warn"
            >
              {photoError}
            </p>
          ) : null}
        </section>

        {/* To skjulte felt: kameraet direkte (`capture`), og albumet uten
            `capture`, så telefonen lar barnet velge et bilde som alt er tatt. */}
        <input
          ref={cameraInputRef}
          data-testid="chore-complete-photo-input"
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={onFileChange}
        />
        <input
          ref={albumInputRef}
          data-testid="chore-complete-album-input"
          type="file"
          accept="image/*"
          hidden
          onChange={onFileChange}
        />

        {/* Submit error — inline, never a toast (#74). */}
        <SheetError message={submitError} data-testid="chore-complete-submit-error" />

        <Button
          variant="primary"
          size={56}
          data-testid="chore-complete-submit"
          disabled={lockAll}
          onClick={onSubmit}
          className="mt-2 w-full"
        >
          {submitting ? (
            <span
              data-testid="chore-complete-submit-spinner"
              aria-hidden
              className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none"
            />
          ) : (
            t('chores.complete.submit')
          )}
        </Button>
      </div>
    </BottomSheet>
  );
}
